import React from "react";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ListPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalCount?: number;
  limit?: number;
  onLimitChange?: (limit: number) => void;
}

export function ListPagination({ 
  currentPage, 
  totalPages, 
  onPageChange,
  totalCount,
  limit,
  onLimitChange
}: ListPaginationProps) {
  const [jumpPage, setJumpPage] = React.useState<string>("");

  if (totalPages <= 0 && (!totalCount || totalCount === 0)) return null;

  const handleJumpPage = () => {
    const pageNum = parseInt(jumpPage, 10);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      onPageChange(pageNum);
      setJumpPage("");
    }
  };

  // Generate page numbers to show
  const getVisiblePages = () => {
    const pages: (number | "ellipsis")[] = [];
    
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    pages.push(1);
    
    if (currentPage > 3) {
      pages.push("ellipsis");
    }
    
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    if (currentPage < totalPages - 2) {
      pages.push("ellipsis");
    }
    
    pages.push(totalPages);
    
    return pages;
  };

  const pages = getVisiblePages();
  const currentLimit = limit || 10;
  
  const startEntry = totalCount === 0 ? 0 : (currentPage - 1) * currentLimit + 1;
  const endEntry = totalCount === 0 ? 0 : Math.min(currentPage * currentLimit, totalCount || 0);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between w-full gap-3 sm:gap-4 text-xs sm:text-sm text-muted-foreground py-1">
      <div className="flex items-center gap-3 sm:gap-4 flex-wrap justify-center sm:justify-start">
        {onLimitChange && limit ? (
          <div className="flex items-center gap-1.5 text-xs">
            <span>Show</span>
            <Select value={limit.toString()} onValueChange={(v) => onLimitChange(Number(v))}>
              <SelectTrigger className="h-8 w-[68px] rounded-lg bg-background border-border text-foreground text-xs font-semibold">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from(new Set([Number(limit || 10), 5, 10, 25, 50, 100])).sort((a, b) => a - b).map((opt) => (
                  <SelectItem key={opt} value={opt.toString()}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span>entries</span>
          </div>
        ) : null}

        {totalCount !== undefined ? (
          <span className="text-xs font-medium text-muted-foreground">
            Showing {startEntry} to {endEntry} of {totalCount} entries
          </span>
        ) : null}
      </div>

      <div className="flex items-center justify-center sm:justify-end flex-nowrap gap-2 sm:gap-3 shrink-0 whitespace-nowrap">
        <Pagination className="w-auto mx-0 shrink-0">
          <PaginationContent className="gap-1 flex-nowrap">
            <PaginationItem>
              <PaginationPrevious 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  if (currentPage > 1) onPageChange(currentPage - 1);
                }}
                className={currentPage <= 1 ? "pointer-events-none opacity-50 h-8 px-2.5 text-xs rounded-lg" : "cursor-pointer h-8 px-2.5 text-xs rounded-lg"}
              />
            </PaginationItem>
            
            {pages.map((page, i) => (
              <PaginationItem key={i}>
                {page === "ellipsis" ? (
                  <PaginationEllipsis />
                ) : (
                  <PaginationLink 
                    href="#"
                    isActive={page === currentPage}
                    className="h-8 w-8 text-xs rounded-lg font-semibold"
                    onClick={(e) => {
                      e.preventDefault();
                      onPageChange(page as number);
                    }}
                  >
                    {page}
                  </PaginationLink>
                )}
              </PaginationItem>
            ))}
            
            <PaginationItem>
              <PaginationNext 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  if (currentPage < totalPages) onPageChange(currentPage + 1);
                }}
                className={currentPage >= totalPages ? "pointer-events-none opacity-50 h-8 px-2.5 text-xs rounded-lg" : "cursor-pointer h-8 px-2.5 text-xs rounded-lg"}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>

        {totalPages > 1 && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground border-l border-border/60 pl-2.5 sm:pl-3 shrink-0 whitespace-nowrap">
            <span className="font-medium">Go to:</span>
            <input
              type="number"
              min={1}
              max={totalPages}
              value={jumpPage}
              onChange={(e) => setJumpPage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleJumpPage();
              }}
              placeholder="#"
              title="Enter page number to jump directly"
              className="w-11 h-8 text-center rounded-lg border border-input bg-background font-bold text-foreground text-xs outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <button
              type="button"
              onClick={handleJumpPage}
              className="h-8 px-2.5 rounded-lg bg-primary text-primary-foreground hover:opacity-90 text-xs font-bold transition cursor-pointer shadow-2xs shrink-0"
            >
              Go
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
